import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseApiWithAuth } from "@/utilities/reduxToolkit";

export interface FieldTemplateData {
  id: string;
  name: string;
  type: string;
  category: string;
  details: Record<string, any>;
  description?: string;
  companyId?: string | null;
}

export interface CreateFieldTemplateRequest {
  name: string;
  type: string;
  category: string;
  description?: string;
  details?: Record<string, any>;
}

export interface UpdateFieldTemplateRequest {
  id: string;
  name?: string;
  type?: string;
  category?: string;
  description?: string;
  details?: Record<string, any>;
}

export const libraryApi = createApi({
  ...createBaseApiWithAuth({
    reducerPath: "libraryApi",
    baseUrl: "/api/library",
  }),
  reducerPath: "libraryApi",
  tagTypes: ["FieldTemplates"],
  endpoints: (builder) => ({
    getFields: builder.query<any, { include?: string; limit?: number }>({
      query: (args) => {
        const params = new URLSearchParams();

        if (args.include) {
          params.append("include", args.include);
        }

        if (args.limit) {
          params.append("limit", args.limit?.toString());
        }

        return `/fields?${params.toString()}`;
      },
      providesTags: [{ type: "FieldTemplates" }],
    }),
    getRoles: builder.query<any, { include?: string; limit?: number }>({
      query: (args) => {
        const params = new URLSearchParams();

        if (args.include) {
          params.append("include", args.include);
        }

        if (args.limit) {
          params.append("limit", args.limit?.toString());
        }

        return `/roles?${params.toString()}`;
      },
    }),
    createFieldTemplate: builder.mutation<
      { data: FieldTemplateData },
      CreateFieldTemplateRequest
    >({
      query: (data) => ({
        url: "/fields",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "FieldTemplates" }],
    }),
    updateFieldTemplate: builder.mutation<
      { data: FieldTemplateData },
      UpdateFieldTemplateRequest
    >({
      query: ({ id, ...data }) => ({
        url: `/fields/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: [{ type: "FieldTemplates" }],
    }),
    deleteFieldTemplate: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `/fields/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "FieldTemplates" }],
    }),
  }),
});

export const {
  useGetFieldsQuery,
  useGetRolesQuery,
  useCreateFieldTemplateMutation,
  useUpdateFieldTemplateMutation,
  useDeleteFieldTemplateMutation,
} = libraryApi;
